import { createReducer } from "@reduxjs/toolkit";
import arrayMove from "array-move";

import { assert } from "../../../asserts/index.ts";
import { type IdsState, type TriggerAction } from "../../../schemas/index.ts";
import { InvalidActionError } from "../../InvalidActionError.ts";
import { removeNodeAction } from "../actions.ts";
import { removeIdFromLookup } from "./helpers/lookups.ts";
import {
  addHandlerAction,
  addResponseAction,
  addTriggerAction,
  removeHandlerAction,
  removeResponseAction,
  removeTriggerAction,
  reorderHandlerAction,
  reorderResponseAction,
  reorderTriggerAction,
} from "./triggerActions.ts";
export const idsInitialState: IdsState = {
  triggerIdsByNodeId: {},
  handlerIdsByTriggerId: {},
  responseIdsByHandlerId: {},
  triggerIdByHandlerId: {},
  handlerIdByResponseId: {},
  nodeIdByTriggerId: {},
};

interface Options {
  isNodeIdValid?: (nodeId: number) => boolean;
}

export const idsReducerCreate = ({ isNodeIdValid }: Options) =>
  createReducer(idsInitialState, (builder) => {
    const getHandlerIdForResponseIdCreate = (state: IdsState, action: TriggerAction) => (responseId: number) => {
      const handlerId = state.handlerIdByResponseId[responseId];
      if (!handlerId) {
        throw new InvalidActionError(action, `Handler not found for response with id: ${responseId}.`);
      }
      return handlerId;
    };

    const getTriggerIdForHandlerIdCreate = (state: IdsState, action: TriggerAction) => (handlerId: number) => {
      const triggerId = state.triggerIdByHandlerId[handlerId];
      if (!triggerId) {
        throw new InvalidActionError(action, `Trigger not found for handler with id: ${handlerId}.`);
      }
      return triggerId;
    };

    const getNodeIdForTriggerIdCreate = (state: IdsState, action: TriggerAction) => (triggerId: number) => {
      const nodeId = state.nodeIdByTriggerId[triggerId];
      if (!nodeId) {
        throw new InvalidActionError(action, `Trigger not found with id: ${triggerId}.`);
      }
      return nodeId;
    };

    builder.addCase(addTriggerAction, (state, action) => {
      const { nodeId, id: triggerId } = action.payload;
      if (!isNodeIdValid?.(nodeId)) {
        throw new InvalidActionError(action, `Node not found with id: ${nodeId}.`);
      }
      if (!(nodeId in state.triggerIdsByNodeId)) {
        state.triggerIdsByNodeId[nodeId] = [];
      }
      state.triggerIdsByNodeId[nodeId].push(triggerId);
      state.nodeIdByTriggerId[triggerId] = nodeId;
    });

    builder.addCase(addHandlerAction, (state, action) => {
      const { triggerId, id: handlerId } = action.payload;
      if (!(triggerId in state.handlerIdsByTriggerId)) {
        state.handlerIdsByTriggerId[triggerId] = [];
      }
      state.handlerIdsByTriggerId[triggerId].push(handlerId);
      state.triggerIdByHandlerId[handlerId] = triggerId;
    });

    builder.addCase(addResponseAction, (state, action) => {
      const { handlerId, id: responseId } = action.payload;
      if (!(handlerId in state.responseIdsByHandlerId)) {
        state.responseIdsByHandlerId[handlerId] = [];
      }
      state.responseIdsByHandlerId[handlerId].push(responseId);
      state.handlerIdByResponseId[responseId] = handlerId;
    });

    builder.addCase(removeTriggerAction, (state, action) => {
      const { id: triggerId } = action.payload;
      const nodeId = getNodeIdForTriggerIdCreate(state, action)(triggerId);
      delete state.handlerIdsByTriggerId[triggerId];
      delete state.nodeIdByTriggerId[triggerId];
      removeIdFromLookup(state.triggerIdsByNodeId, nodeId, triggerId);
    });

    builder.addCase(removeHandlerAction, (state, action) => {
      const { id: handlerId } = action.payload;
      const triggerId = getTriggerIdForHandlerIdCreate(state, action)(handlerId);
      delete state.responseIdsByHandlerId[handlerId];
      delete state.triggerIdByHandlerId[handlerId];
      removeIdFromLookup(state.handlerIdsByTriggerId, triggerId, handlerId);
    });

    builder.addCase(removeResponseAction, (state, action) => {
      const { id: responseId } = action.payload;
      const handlerId = getHandlerIdForResponseIdCreate(state, action)(responseId);

      delete state.handlerIdByResponseId[responseId];
      removeIdFromLookup(state.responseIdsByHandlerId, handlerId, responseId);
    });

    builder.addCase(reorderResponseAction, (state, action) => {
      const { id: responseId, newIndex } = action.payload;
      if (newIndex < 0) {
        throw new InvalidActionError(action, `New index must be greater than or equal to 0. Got ${newIndex}.`);
      }
      const handlerId = getHandlerIdForResponseIdCreate(state, action)(responseId);
      const responseIds = state.responseIdsByHandlerId[handlerId] ?? [];
      const index = responseIds.findIndex((id) => id === responseId);
      if (index === -1) {
        throw new InvalidActionError(
          action,
          `No response found in handler: ${handlerId} with id "${responseId}" to move.`,
        );
      }
      state.responseIdsByHandlerId[handlerId] = arrayMove(responseIds, index, newIndex);
    });

    builder.addCase(reorderHandlerAction, (state, action) => {
      const { id: handlerId, newIndex } = action.payload;
      if (newIndex < 0) {
        throw new InvalidActionError(action, `New index must be greater than or equal to 0. Got ${newIndex}.`);
      }
      const triggerId = getTriggerIdForHandlerIdCreate(state, action)(handlerId);
      const handlerIds = state.handlerIdsByTriggerId[triggerId] ?? [];
      const index = handlerIds.findIndex((id) => id === handlerId);
      assert(index !== -1);
      state.handlerIdsByTriggerId[triggerId] = arrayMove(handlerIds, index, newIndex);
    });

    builder.addCase(reorderTriggerAction, (state, action) => {
      const { id: triggerId, newIndex } = action.payload;
      if (newIndex < 0) {
        throw new InvalidActionError(action, `New index must be greater than or equal to 0. Got ${newIndex}.`);
      }
      const nodeId = getNodeIdForTriggerIdCreate(state, action)(triggerId);
      const triggerIds = state.triggerIdsByNodeId[nodeId] ?? [];
      const index = triggerIds.findIndex((id) => id === triggerId);
      assert(index !== -1);
      state.triggerIdsByNodeId[nodeId] = arrayMove(triggerIds, index, newIndex);
    });

    builder.addCase(removeNodeAction, (state, action) => {
      const { id: nodeId } = action.payload;
      delete state.triggerIdsByNodeId[nodeId];
    });
  });
